import React from "react";
import Loader from "./Loader";

interface VerticalTableProps {
  headers: string[];
  data: { category: string; values: string[] }[];
  loading: boolean;
  isComparative?: boolean;
  branchCount?: number;
  branchColors?: Record<string, { bgColor: string; textColor: string; borderColor: string }>;
  branchIds?: string[];
}

const VerticalTable2: React.FC<VerticalTableProps> = ({ 
  headers, 
  data, 
  loading,
  isComparative = false,
  branchCount = 1,
  branchColors = {},
  branchIds = []
}) => {
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Loader />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <p>No data available for the selected criteria</p>;
  }

  // Create a mapping of column index to branch ID
  const getBranchIdForColumn = (colIndex: number): string | null => {
    if (!isComparative || !branchIds.length) return null;
    const branchIndex = (colIndex - 1) % branchCount;
    return branchIds[branchIndex] || null;
  };

  const hexToRGBA = (hex: string, alpha: number): string => {
  const match = hex.replace("#", "").match(/.{1,2}/g);
  if (!match || match.length < 3) return hex;
  const [r, g, b] = match.map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isTotalColumn = (colIdx: number) => colIdx === headers.length - 1;


  // Get style for a cell based on branch
  // Get style for a cell based on branch
const getCellStyle = (colIdx: number, isCategory = false, isHeader = false) => {
  // Exclude the last column (Total column) from styling
  if (!isComparative || isCategory || colIdx === headers.length - 1) return {};

  const branchIdx = (colIdx - 1) % branchCount;
  const branchId = branchIds[branchIdx];
  const color = branchColors[branchId];

  if (!color || branchIdx < 0) return {};

  return isHeader
    ? {
        backgroundColor: color.bgColor,
        color: color.textColor,
        position: "relative" as const,
        borderBottom: `2px solid ${color.borderColor}`,
      }
    : {
        backgroundColor: `${color.bgColor}`,
        borderLeft: `3px solid ${color.borderColor}`,
        position: "relative" as const,
      };
};


  return (
    <div className="table-responsive" style={{ overflowX: "auto" }}>
      <table
        className="table table-bordered table-hover"
        style={{
          margin: "20px",
          whiteSpace: "nowrap",
        }}
      >
        <thead>
          {isComparative && (
            <>
              {/* Period row */}
              <tr style={{ fontSize: 12, fontWeight: 300 }}>
                <th rowSpan={2} className="tableHead" style={{ 
                  textAlign: "center", 
                  fontWeight: 500, 
                  padding: "8px",
                  backgroundColor: "#f8f9fa"
                }}>
                  {headers[0]}
                </th>
                {headers.slice(1, -1).map((header, index) => {
                  const bucketIndex = Math.floor(index / branchCount);
                  if (index % branchCount === 0) {
                    return (
                      <th 
                        key={`bucket-${bucketIndex}`} 
                        colSpan={branchCount}
                        className="tableHead" 
                        style={{ 
                          textAlign: "center", 
                          fontWeight: 500, 
                          padding: "8px",
                          backgroundColor: "#f8f9fa"
                        }}
                      >
                        {header.split('\n')[0]}
                      </th>
                    );
                  }
                  return null;
                })}
                <th rowSpan={2} className="tableHead" style={{ 
                  textAlign: "center", 
                  fontWeight: 500, 
                  padding: "8px",
                  backgroundColor: "#f8f9fa"
                }}>
                  {headers[headers.length - 1]}
                </th>
              </tr>
              {/* Branch row */}
              <tr style={{ fontSize: 12, fontWeight: 300 }}>
                {headers.slice(1, -1).map((header, index) => {
                  const style = getCellStyle(index + 1, false, true); // ✅ “header = true”

                  const branchId = getBranchIdForColumn(index + 1);
                  const color = branchId ? branchColors[branchId] : null;
                  
                  return (
                    <th
                      className="tableHead"
                      style={{
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "8px",
                        ...style,
                        position: 'relative'
                      }}
                      key={`branch-${index}`}
                    >
                      {(header.split('\n')[1] ?? '').replace(/[()]/g, '')}
                      {color && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '3px',
                            backgroundColor: color.borderColor
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </>
          )}
          {!isComparative && (
            <tr style={{ fontSize: 12, fontWeight: 300 }}>
              {headers.map((header, index) => (
                <th
                  className="tableHead"
                  style={{
                    textAlign: "center",
                    fontWeight: 500,
                    padding: "8px",
                    backgroundColor: "#f8f9fa"
                  }}
                  key={index}
                >
                  {header}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody style={{ fontSize: 13, fontWeight: 400 }}>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td
                className="text-start align-middle ps-3"
                style={{ 
                  fontWeight: 500, 
                  padding: "8px",
                  backgroundColor: "#f8f9fa"
                }}
              >
                {row.category}
              </td>
              {row.values.map((value, colIndex) => {
                const style = getCellStyle(colIndex + 1);
                const isLastInGroup = isComparative && (colIndex + 1) % branchCount === 0;
                
                return (
                  <td
                    key={colIndex}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      ...style,
                      borderRight: isLastInGroup ? '2px solid #dee2e6' : '1px solid #dee2e6'
                    }}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VerticalTable2;